import { Theme, css } from '@emotion/react';
import { useActor } from '@xstate/react';
import { useCombobox } from 'downshift';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { Address } from 'api/api';

import { useOutsideClick } from '~src/commons/utils/hooks';
import { useOutSideIframeClick } from '~src/commons/utils/hooks/checkout-hooks';
import { useTranslations } from '~src/commons/utils/translations/translate';
import { InfoOutlineIcon } from '~src/icons/info-outline-icon';
import { fontStyles } from '~src/theme/fontStyles';

import { useAddressFormBusinessLogic } from '~address-form/context/use-address-form';

import { AddressFormFields, PlaceQueryItem } from '../../types';
import { isStreetNumberMissing } from './autocomplete-helpers';
import { AutocompleteInput } from './autocomplete-input';
import { Results } from './results';

export type ComboboxListItem = PlaceQueryItem | 'SHOW_ALL';

interface AutocompleteProps {
  onPlaceSelect: (values: Address) => void;
  warning?: boolean;
}

const SUGGESTIONS_LIMIT = 15;

export const Autocomplete: FC<AutocompleteProps> = ({ onPlaceSelect, warning }) => {
  const { tICU } = useTranslations();
  const { autocompleteService, addressFormValidationConfig, addressFormLayout, extendedAddress } =
    useAddressFormBusinessLogic();
  const [autocompleteState, sendEventToMachine] = useActor(autocompleteService);
  const { setValue, getValues } = useFormContext<AddressFormFields>();
  const [showAll, setShowAll] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);

  const listItems: Array<ComboboxListItem> = showAll
    ? autocompleteState.context.places
    : autocompleteState.context.places.slice(0, SUGGESTIONS_LIMIT);

  if (listItems.length === SUGGESTIONS_LIMIT && !showAll) {
    listItems.push('SHOW_ALL');
  }

  useEffect(() => {
    listRef.current?.scrollTo(0, 0);
  }, [autocompleteState.context.places]);

  useEffect(() => {
    autocompleteService.onTransition(data => {
      if (data.event.type === 'GET_PLACE_DETAILS_REQUEST_SUCCESS') {
        onPlaceSelect(data.event.data);
      } else if (data.event.type === 'CLEAR_INPUT') {
        setValue('address_lines', ['']);
        setValue('street', '');
        setValue('street_number', '');
        setShowAll(false);
      } else if (data.event.type === 'KEYSTROKE') {
        setValue('address_lines', [data.event.query]);
        setValue('street', '');
        setValue('street_number', '');
        setShowAll(false);
      }
    });
  }, []);

  const optionalStreetNumber = useMemo(
    () =>
      addressFormLayout.find(
        field =>
          field.name === 'address_lines' && field.type === 'optional_address_line_street_number'
      ),
    [addressFormLayout]
  );

  const displayHint = useMemo(() => {
    if (!optionalStreetNumber) return false;

    const inputVal = getValues('address_lines.0');
    if (!inputVal) return false;

    return isStreetNumberMissing(inputVal);
  }, [getValues('address_lines.0'), optionalStreetNumber, isStreetNumberMissing]);

  const { getMenuProps, getInputProps, getComboboxProps, highlightedIndex, getItemProps, isOpen } =
    useCombobox({
      isOpen: autocompleteState.matches('focused'),
      items: listItems,
      onStateChange: change => {
        if (change.type === useCombobox.stateChangeTypes.InputBlur) {
          sendEventToMachine('BLUR');
        }
      },
      onSelectedItemChange: ({ selectedItem }) => {
        if (selectedItem) {
          if (selectedItem === 'SHOW_ALL') {
            if (autocompleteState.context.places.length > SUGGESTIONS_LIMIT) {
              setShowAll(true);
            }
          } else if ('container_id' in selectedItem) {
            sendEventToMachine({
              type: 'SELECT_PLACES_CONTAINER',
              address: selectedItem.description.main,
              containerId: selectedItem.container_id,
            });
          } else {
            sendEventToMachine({
              type: 'SELECT_PLACE',
              address: selectedItem!.description.main,
              placeId: selectedItem!.id!,
            });
          }
        }
      },
    });

  const wrapperRef = useOutsideClick(() => {
    sendEventToMachine('BLUR');
  }, []);

  useOutSideIframeClick(() => {
    sendEventToMachine('BLUR');
  }, []);

  return (
    <>
      <div ref={wrapperRef}>
        <div css={styles.inputWrapper} {...getComboboxProps()}>
          <AutocompleteInput
            getInputProps={getInputProps}
            machineService={autocompleteService}
            validationConfig={addressFormValidationConfig}
            warning={warning}
            optionalStreetNumber={!!optionalStreetNumber}
          />
          <Results
            highlightedIndex={highlightedIndex}
            getItemProps={getItemProps}
            getMenuProps={getMenuProps}
            machineService={autocompleteService}
            listItems={listItems}
            isOpen={isOpen}
            ref={listRef}
          />
        </div>
        {displayHint && !extendedAddress && (
          <div css={styles.hint}>
            <InfoOutlineIcon />
            <span css={styles.hintText}>{tICU('widget.delivery_address.ADDRESS_LINE_HINT')}</span>
          </div>
        )}
      </div>
    </>
  );
};

const styles = {
  inputWrapper: css({
    position: 'relative',
  }),
  hint: (theme: Theme) =>
    css({
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      ...fontStyles.px14_20,
      color: theme.darkGrey,
      marginTop: '8px',
      marginBottom: '-4px',
    }),
  hintText: {
    marginLeft: '8px',
  },
};
